// src/sentry.js
import * as Sentry from '@sentry/react';

/** Safe, compact stringifier for console args / breadcrumbs */
function safeStringify(value) {
  const seen = new WeakSet();
  try {
    return JSON.stringify(
      value,
      (_, v) => {
        if (typeof v === 'object' && v !== null) {
          if (seen.has(v)) return '[Circular]';
          seen.add(v);
        }
        if (typeof v === 'function') return '[Function]';
        if (v instanceof Error) {
          return { name: v.name, message: v.message, stack: v.stack };
        }
        return v;
      }
    );
  } catch {
    try {
      return String(value);
    } catch {
      return '[Unserializable]';
    }
  }
}

export function initSentry() {
  // Only initialize Sentry in production (not staging)
  const appEnv = process.env.REACT_APP_ENV;
  if (appEnv !== 'production') {
    return;
  }

  const dsn = process.env.REACT_APP_SENTRY_URL;

  if (!dsn) {
    console.warn('Sentry DSN not found. Error tracking & logs are disabled.');
    return;
  }

  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV || 'development',

    // You said “errors only” for perf traces; logs don’t need tracing enabled.
    enableTracing: false,
    tracesSampleRate: 0,

    // Keep more console breadcrumbs on events (optional)
    maxBreadcrumbs: 200,

    // 👇 NEW in v9: use the function `captureConsoleIntegration`
    // Capture ALL console levels (log/info/warn/error/debug)
    integrations: [
      Sentry.captureConsoleIntegration({
        levels: ['log', 'info', 'warn', 'error', 'debug'],
      }),
    ],

    // Scrub/compact console breadcrumbs so they’re readable in Issues
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'console' && breadcrumb.data?.arguments) {
        try {
          breadcrumb.data.arguments = breadcrumb.data.arguments.map((a) =>
            typeof a === 'string' ? a : safeStringify(a)
          );
          if (!breadcrumb.message) {
            breadcrumb.message = breadcrumb.data.arguments.join(' ');
          }
        } catch {
          // ignore
        }
      }
      return breadcrumb;
    },

    // Final scrub for events (add custom redactions here if you need)
    beforeSend(event) {
      return event;
    },
  });
}

/** Helper for manual try/catch */
export function catchErrorsWithScope(error, extra) {
  Sentry.withScope((scope) => {
    if (extra && typeof extra === 'object') scope.setExtras(extra);
    Sentry.captureException(error);
  });
}

// Export if you want to use Sentry’s error boundary instead of your own
export const SentryErrorBoundary = Sentry.ErrorBoundary;
